import { useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';

import { ContextActionsMenu } from '../../../../components/ContextActionsMenu';
import { Button, Modal } from '../../../../components/ui';
import type { Patient, StageKind } from '../../../../types/admin';
import { type FunnelFilters } from '../../hooks/useFunnelColumn';
import { useMovePatientStage } from '../../hooks/useMovePatientStage';
import { FunnelColumn } from './FunnelColumn';
import styles from './funnel.module.scss';

/** Pipeline step order (matches the backend STAGE_KIND_ORDER). */
const STAGE_KINDS: readonly StageKind[] = [
  'preparing',
  'modeling',
  'payment',
  'manufacturing_delivery',
  'wearing_aligners',
];

type PendingMove = {
  readonly patient: Patient;
  readonly fromStage: StageKind;
  readonly toStage: StageKind;
};

type CardContextMenu = {
  readonly patient: Patient;
  readonly x: number;
  readonly y: number;
};

type PatientFunnelBoardProps = {
  readonly filters: FunnelFilters;
  readonly onOpenPatient: (patientId: string) => void;
  readonly onChatPatient: (patientId: string) => void;
  readonly onRequestDelete: (patient: Patient) => void;
};

export function PatientFunnelBoard({
  filters,
  onOpenPatient,
  onChatPatient,
  onRequestDelete,
}: PatientFunnelBoardProps) {
  const { t } = useTranslation();
  const moveMutation = useMovePatientStage(filters);
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
  const [contextMenu, setContextMenu] = useState<CardContextMenu | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) {
      return;
    }
    const data = active.data.current as { patient: Patient; fromStage: StageKind } | undefined;
    if (!data) {
      return;
    }
    const toStage = over.id as StageKind;
    if (!STAGE_KINDS.includes(toStage) || toStage === data.fromStage) {
      return;
    }
    setPendingMove({ patient: data.patient, fromStage: data.fromStage, toStage });
  }

  function closeMoveModal() {
    if (moveMutation.isPending) {
      return;
    }
    setPendingMove(null);
    moveMutation.reset();
  }

  function confirmMove() {
    if (!pendingMove) {
      return;
    }
    moveMutation.mutate(pendingMove, {
      onSuccess: () => {
        setPendingMove(null);
      },
    });
  }

  const pendingName = pendingMove
    ? `${pendingMove.patient.first_name} ${pendingMove.patient.last_name}`.trim()
    : '';

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragEnd={handleDragEnd}>
        <div className={styles.board}>
          {STAGE_KINDS.map((stageKind) => (
            <FunnelColumn
              key={stageKind}
              stageKind={stageKind}
              filters={filters}
              onOpenPatient={onOpenPatient}
              onPatientContextMenu={(patient, event: ReactMouseEvent) => {
                event.preventDefault();
                setContextMenu({ patient, x: event.clientX, y: event.clientY });
              }}
            />
          ))}
        </div>
      </DndContext>

      {contextMenu && (
        <ContextActionsMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onOpen={() => onOpenPatient(contextMenu.patient.id)}
          onChat={() => onChatPatient(contextMenu.patient.id)}
          onDelete={() => onRequestDelete(contextMenu.patient)}
        />
      )}

      <Modal
        open={pendingMove !== null}
        onClose={closeMoveModal}
        title={t('clients.funnel.moveConfirmTitle')}
        subtitle={
          pendingMove
            ? t('clients.funnel.moveConfirmBody', {
                name: pendingName,
                step: t(`stages.${pendingMove.toStage}`),
              })
            : undefined
        }
        size="sm"
        closeOnBackdrop={!moveMutation.isPending}
        footer={
          <>
            <Button variant="secondary" onClick={closeMoveModal} disabled={moveMutation.isPending}>
              {t('clients.cancel')}
            </Button>
            <Button variant="primary" onClick={confirmMove} disabled={moveMutation.isPending}>
              {moveMutation.isPending
                ? t('clients.funnel.moving')
                : t('clients.funnel.moveConfirmCta')}
            </Button>
          </>
        }
      >
        {moveMutation.isError ? (
          <p className={styles.moveError}>{t('clients.funnel.moveError')}</p>
        ) : null}
      </Modal>
    </>
  );
}
